/**
 * An assigned assessment, as the shared detail screen shows it.
 *
 * The Assessments tab opens its rows in the same detail screen homework uses
 * (so a hand-in type can actually be handed in). This turns the API row into
 * that screen's shape WITHOUT dropping what makes it an assessment - type,
 * marks, timing, class, teacher - and lists those facts in reading order.
 * Both the native and web screens use it, so the two cannot drift apart.
 */
import { HomeworkItem } from '../types';
import { formatLocalDateOnly, formatLocalDateTime } from './dateUtils';

const pick = (row: any, camel: string) => row?.[camel] ?? row?.[camel.charAt(0).toUpperCase() + camel.slice(1)];

/** "Graded" / "Submitted" from the server; anything else is still to do. */
function toStatus(raw: unknown): HomeworkItem['status'] {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'graded') return 'graded';
  if (s === 'submitted') return 'submitted';
  return 'pending';
}

/** GET /Assessment/parent/class/{id} row -> the detail screen's item. */
export function toAssessmentDetailItem(row: any): HomeworkItem {
  const files: any[] = pick(row, 'teacherFiles') ?? [];
  return {
    id: pick(row, 'id'),
    subject: pick(row, 'subjectName') || '',
    title: pick(row, 'title') || '',
    description: pick(row, 'instructions') || '',
    dueDate: pick(row, 'dueDate') ?? '',
    assignedDate: pick(row, 'assignedDate') ?? '',
    status: toStatus(pick(row, 'status')),
    teacherName: pick(row, 'teacherName') || '',
    category: pick(row, 'categoryName') || undefined,
    source: 'assessment',
    // A timed paper is sat in class, so its detail shows a note instead of
    // a file picker. Anything else is collected online.
    collectsUpload: pick(row, 'deliveryShape') !== 'Paper',
    teacherFiles: files.map(f => ({
      id: String(pick(f, 'id') ?? ''),
      fileUrl: pick(f, 'fileUrl') ?? '',
      fileName: pick(f, 'fileName') ?? 'Attachment',
    })),
    className: pick(row, 'className') || undefined,
    outOfMarks: pick(row, 'outOfMarks') ?? null,
    time: pick(row, 'time') ?? null,
    scheduleStartDateTime: pick(row, 'scheduleStartDateTime') ?? null,
    durationMinutes: pick(row, 'durationMinutes') ?? null,
  };
}

export interface AssessmentFact {
  /** Feather icon name. */
  icon: string;
  label: string;
  value: string;
  /** Due date already passed with nothing handed in. */
  alert?: boolean;
}

/** "09:30:00" (a .NET TimeSpan) -> "9:30 AM". */
function formatClockTime(value?: string | null): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value ?? ''));
  if (!m) return '';
  const d = new Date(2000, 0, 1, Number(m[1]), Number(m[2]));
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** "1 hr 30 min" / "45 min". */
function formatDuration(minutes?: number | null): string {
  if (!minutes || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} hr` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
}

/** Whether the due date is behind us and nothing has been handed in. */
export function isPastDue(item: HomeworkItem): boolean {
  if (item.status !== 'pending' || !item.dueDate) return false;
  const due = new Date(`${String(item.dueDate).slice(0, 10)}T23:59:59`);
  return !isNaN(due.getTime()) && due < new Date();
}

/** The facts worth showing, in reading order; empty ones are left out. */
export function assessmentFacts(item: HomeworkItem): AssessmentFact[] {
  const facts: AssessmentFact[] = [];
  const add = (icon: string, label: string, value: string, alert = false) => {
    if (value) facts.push({ icon, label, value, alert });
  };

  add('user', 'Teacher', item.teacherName || 'Not specified');
  add('tag', 'Type', item.category || 'Assessment');
  add('book', 'Subject', item.subject);
  add('users', 'Class', item.className || '');
  add('calendar', 'Assigned', formatLocalDateOnly(item.assignedDate));
  add('flag', 'Due', formatLocalDateOnly(item.dueDate), isPastDue(item));
  // A timed paper's own start beats the plain time-of-day.
  add(
    'clock',
    'Starts',
    item.scheduleStartDateTime
      ? formatLocalDateTime(item.scheduleStartDateTime)
      : formatClockTime(item.time),
  );
  add('watch', 'Duration', formatDuration(item.durationMinutes));
  add('award', 'Total marks', item.outOfMarks != null ? String(Number(item.outOfMarks)) : '');
  return facts;
}

/** The words for the status badge. */
export function statusLabel(item: HomeworkItem): string {
  if (item.status === 'graded') return 'Graded';
  if (item.status === 'submitted') return 'Submitted';
  return isPastDue(item) ? 'Overdue' : 'Pending';
}
